"use client";

/**
 * SkillArtifactCard — the small clickable card that renders on a user message
 * bubble when that message launched a skill (no-blob skill launch). Clicking it
 * opens a mobile-friendly modal that shows the same content as the skill's
 * dedicated page (/skills/[id]): name, description, explanation, data sources.
 *
 * The card stores only the skill_id on the message; it lazily fetches the skill
 * (public list, then personal list as fallback) to resolve the name + body.
 *
 * Copy/style rules: NO gray text, NO em dashes, slim buttons.
 */

import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { AnimatePresence, motion } from "framer-motion";
import { Sparkles, X } from "lucide-react";
import { apiClient, type PublicSkill } from "@/lib/api-client";
import { cleanInstruction, prettySource } from "@/lib/skills-map";

// Tiny module cache so we only hit the API once per skill across all cards.
const SKILL_CACHE = new Map<string, PublicSkill | null>();

async function resolveSkill(skillId: string): Promise<PublicSkill | null> {
  if (SKILL_CACHE.has(skillId)) return SKILL_CACHE.get(skillId) ?? null;
  let found: PublicSkill | null = null;
  try {
    const pub = await apiClient.listPublicSkills({ limit: 100 });
    found = (pub.skills || []).find((s) => s.id === skillId) || null;
  } catch {
    // ignore, try personal next
  }
  if (!found) {
    try {
      const mine = await apiClient.listMySkills();
      found = (mine.skills || []).find((s) => s.id === skillId) || null;
    } catch {
      // ignore
    }
  }
  SKILL_CACHE.set(skillId, found);
  return found;
}

const MD = {
  h1: ({ children }: { children?: React.ReactNode }) => (
    <h1 className="mb-2 mt-6 text-xl font-bold tracking-tight text-white first:mt-0">
      {children}
    </h1>
  ),
  h2: ({ children }: { children?: React.ReactNode }) => (
    <h2 className="mb-2 mt-6 text-lg font-bold tracking-tight text-white first:mt-0">
      {children}
    </h2>
  ),
  h3: ({ children }: { children?: React.ReactNode }) => (
    <h3 className="mb-1.5 mt-5 text-base font-semibold text-white first:mt-0">
      {children}
    </h3>
  ),
  p: ({ children }: { children?: React.ReactNode }) => (
    <p className="mb-3 text-white/90">{children}</p>
  ),
  ul: ({ children }: { children?: React.ReactNode }) => (
    <ul className="mb-3 list-disc space-y-1 pl-5 text-white/90">{children}</ul>
  ),
  ol: ({ children }: { children?: React.ReactNode }) => (
    <ol className="mb-3 list-decimal space-y-1 pl-5 text-white/90">{children}</ol>
  ),
  li: ({ children }: { children?: React.ReactNode }) => <li>{children}</li>,
  strong: ({ children }: { children?: React.ReactNode }) => (
    <strong className="font-semibold text-white">{children}</strong>
  ),
  code: ({ children }: { children?: React.ReactNode }) => (
    <code className="rounded bg-white/[0.06] px-1 py-0.5 text-[13px] text-emerald-300">
      {children}
    </code>
  ),
};

function SkillModal({
  skill,
  onClose,
}: {
  skill: PublicSkill;
  onClose: () => void;
}) {
  const description =
    (skill.description && skill.description.trim()) ||
    cleanInstruction(skill.explanation || skill.agent_instruction || "").slice(
      0,
      240,
    );
  const body = skill.explanation || skill.agent_instruction || "";
  const sources = skill.data_sources || [];

  return createPortal(
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-[120] flex items-end justify-center sm:items-center"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      >
        <div
          className="absolute inset-0 bg-black/70 backdrop-blur-sm"
          onClick={onClose}
        />
        <motion.div
          initial={{ y: 24, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 24, opacity: 0 }}
          transition={{ type: "spring", damping: 26, stiffness: 280 }}
          className="relative flex max-h-[88vh] w-full max-w-2xl flex-col overflow-hidden rounded-t-2xl border border-white/[0.08] bg-[#0b0d10] shadow-2xl sm:max-h-[90vh] sm:rounded-2xl"
        >
          {/* mobile grab handle */}
          <div className="flex justify-center pt-2.5 sm:hidden">
            <span className="h-1 w-9 rounded-full bg-white/20" />
          </div>

          <div className="flex items-start justify-between gap-3 border-b border-white/[0.06] px-4 py-3.5 sm:px-5 sm:py-4">
            <div className="min-w-0 flex-1">
              <div className="mb-1.5 inline-flex items-center gap-1.5 rounded-md bg-emerald-500/10 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-widest text-emerald-300 ring-1 ring-inset ring-emerald-400/20">
                <Sparkles className="size-2.5" />
                Skill
              </div>
              <h2 className="text-base font-bold leading-tight tracking-tight text-white sm:text-lg">
                {skill.name}
              </h2>
              {description && (
                <p className="mt-1 line-clamp-2 text-[12px] leading-snug text-white/80 sm:text-[13px]">
                  {description}
                </p>
              )}
            </div>
            <button
              type="button"
              onClick={onClose}
              className="-mr-1 shrink-0 rounded-md p-1.5 text-white/85 transition-colors hover:bg-white/[0.06] hover:text-white"
              aria-label="Close"
            >
              <X className="size-4" />
            </button>
          </div>

          <div className="overflow-y-auto px-4 py-4 pb-[max(1.25rem,env(safe-area-inset-bottom))] sm:px-5 sm:py-5">
            <article className="text-[14px] leading-[1.6] text-white">
              <ReactMarkdown remarkPlugins={[remarkGfm]} components={MD}>
                {body}
              </ReactMarkdown>
            </article>

            {sources.length > 0 && (
              <div className="mt-8 border-t border-white/[0.06] pt-6">
                <div className="mb-3 text-[10px] font-bold uppercase tracking-widest text-white/85">
                  Data sources used
                </div>
                <div className="flex flex-wrap gap-2">
                  {sources.map((d) => (
                    <span
                      key={d}
                      className="inline-flex items-center gap-2 rounded-md bg-white/[0.02] px-2.5 py-1.5 text-[10px] font-bold uppercase tracking-widest text-white ring-1 ring-inset ring-white/[0.08]"
                    >
                      <span className="size-1.5 rounded-full bg-emerald-400 shadow-[0_0_8px_rgba(52,211,153,0.8)]" />
                      {prettySource(d)}
                    </span>
                  ))}
                </div>
              </div>
            )}

            <a
              href={`/skills/${skill.id}`}
              className="mt-8 inline-flex items-center gap-1.5 rounded-md bg-white/[0.04] px-3 py-2 text-[11px] font-bold uppercase tracking-widest text-white transition-colors hover:bg-white/[0.08]"
            >
              Open full skill page
            </a>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>,
    document.body,
  );
}

export function SkillArtifactCard({ skillId }: { skillId: string }) {
  const [skill, setSkill] = useState<PublicSkill | null>(
    SKILL_CACHE.get(skillId) ?? null,
  );
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    resolveSkill(skillId).then((s) => {
      if (!cancelled) setSkill(s);
    });
    return () => {
      cancelled = true;
    };
  }, [skillId]);

  const label = skill?.name || "Skill";

  return (
    <>
      <button
        type="button"
        onClick={() => {
          // eslint-disable-next-line no-console
          console.log("[SKILL-LAUNCH] artifact card open", { skillId, label });
          setOpen(true);
        }}
        className="group inline-flex h-7 min-w-0 shrink items-center gap-1.5 rounded-md border border-emerald-400/25 bg-emerald-400/[0.08] px-2 text-left shadow-[0_0_18px_rgba(16,185,129,0.08)] transition-colors hover:border-emerald-400/40 hover:bg-emerald-400/[0.14] sm:max-w-[220px]"
      >
        <Sparkles className="size-3 shrink-0 text-emerald-300" />
        <span className="truncate text-[11px] font-semibold text-white">
          {label}
        </span>
        <span className="shrink-0 text-[9px] font-bold uppercase tracking-wider text-emerald-300/70">
          Skill
        </span>
      </button>
      {open && skill && (
        <SkillModal skill={skill} onClose={() => setOpen(false)} />
      )}
    </>
  );
}
